import React, { useState } from "react";
import { FieldPath, FieldValues, Controller, Control } from "react-hook-form";
import { PhoneInput } from "@/components/ui/input/phone-input";
import { Country } from "react-phone-number-input";
import { getCountries } from "react-phone-number-input";

import { cn } from "@/lib/utils";
import FluidImage from "@/components/images/fluid-image";
import { FLUID_CDN_URL } from "@/lib/constants";
import { useTranslation } from "@/hooks/useTranslation";

const toValidCountry = (
  countryCode: string | undefined,
): Country | undefined => {
  if (!countryCode) return undefined;
  const upperCode = countryCode.toUpperCase();

  const validCountries = getCountries();
  const foundCountry = validCountries.find(
    (validCountry) => validCountry === upperCode,
  );

  return foundCountry || undefined;
};

interface FormPhoneInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "name"> {
  control: Control<TFieldValues>;
  name: TName;
  label: string;
  containerStyles?: string;
  inputStyles?: string;
  required?: boolean;
  handleUpdateCartAttributes?: (value: string) => void;
  showErrorIcon?: boolean;
  handleInputChange?: (value: string) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  showErrorMsg?: boolean;
  country?: string;
  phoneErrorMessage?: string;
  inputClassName?: string;
  buttonClassName?: string;
}

export default function FormPhoneInput<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  containerStyles,
  inputStyles,
  handleUpdateCartAttributes,
  required,
  showErrorIcon = true,
  handleInputChange,
  onBlur,
  showErrorMsg = false,
  country = "US",
  phoneErrorMessage,
  inputClassName,
  buttonClassName,
}: FormPhoneInputProps<TFieldValues, TName>) {
  const { t } = useTranslation();
  const [isFocused, setIsFocused] = useState(false);
  const phoneRequiredMessage = t("phone_number_is_required");

  return (
    <Controller
      control={control}
      name={name}
      rules={{
        required: required ? phoneRequiredMessage : false,
        validate: (value) => {
          if (required && (!value || value.trim() === "")) {
            return phoneRequiredMessage;
          }
          return true;
        },
      }}
      render={({ field: { value, onChange }, fieldState: { error } }) => {
        const errorMessage = error?.message || phoneErrorMessage;
        return (
          <div className={cn("space-y-px rounded bg-white", containerStyles)}>
            <div className="relative w-full">
              {label && (
                <label htmlFor={name} className="sr-only">
                  {label}
                </label>
              )}
              <PhoneInput
                defaultCountry={toValidCountry(country)}
                value={value}
                name={name}
                label={label}
                inputClassName={inputClassName}
                buttonClassName={buttonClassName}
                onChange={(value: string) => {
                  onChange(value);
                  handleInputChange?.(value);
                }}
                onFocus={() => setIsFocused(true)}
                onBlur={() => {
                  setIsFocused(false);
                  if (handleUpdateCartAttributes && value) {
                    if (required || (value && value.length > 3)) {
                      handleUpdateCartAttributes(value);
                    }
                  }
                  // Create a synthetic event for compatibility
                  const syntheticEvent = {
                    target: { value: value || "" },
                    currentTarget: { value: value || "" },
                  } as React.FocusEvent<HTMLInputElement>;
                  onBlur?.(syntheticEvent);
                }}
                className={cn(
                  "w-full ring-1 ring-gray-300",
                  error && "shadow-error",
                  inputStyles,
                )}
              />

              {label && (
                <span
                  className={cn(
                    "pointer-events-none absolute top-1/2 left-3 z-30 -translate-y-1/2 transform pl-[60px] text-sm transition-all",
                    "peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-gray-500",
                    "peer-focus:top-1/4 peer-focus:text-xs",
                    "max-w-[calc(100%-5rem)] truncate",
                    errorMessage
                      ? "text-red-400"
                      : "text-gray-400 peer-focus:text-gray-700",
                    (value || isFocused) && "top-1/4 text-xs",
                  )}
                >
                  {errorMessage && showErrorMsg ? errorMessage : label}
                </span>
              )}
              {error && showErrorIcon && (
                <FluidImage
                  src={`${FLUID_CDN_URL}/images/checkout/icons/circle-exclamation.svg`}
                  alt="error"
                  width={14}
                  height={14}
                  className="red-icons absolute top-1/2 right-3 z-20 -translate-y-1/2 transform"
                />
              )}
            </div>
          </div>
        );
      }}
    />
  );
}
